// Éditeur du plan d'un marché (responsable des marchés, droit modifier) : dimensions du plan, une
// ligne par zone et par emplacement avec sa position et sa taille en mètres, éléments de repère et
// cotes ; les emplacements enregistrés mais absents du plan peuvent y être placés. Enregistrement
// par PUT /marches/:id/plan avec la version connue : un plan modifié entre-temps est refusé
// (409 plan_modifie) et l'éditeur le dit ; un plan incohérent (400) affiche les points en cause.
import { TYPES_ELEMENT_PLAN, schemaPlanMarche, type PlanMarche } from '@cc/contrats';
import { Bandeau, Bouton, Champ, Panneau, Selection } from '@cc/ui';
import { useState, type ReactNode } from 'react';
import { ErreurApi } from '../../api/erreurs';
import { useAction } from '../../commun/useAction';
import { INVALIDER_MARCHES, modifierPlan } from './api';
import type { MarcheDetail } from './types';
import styles from './marches.module.css';

type Rect = { x: number; y: number; largeur: number; hauteur: number };
type Brouillon = {
  largeur: number;
  hauteur: number;
  zones: ({ code: string } & Rect)[];
  emplacements: ({ reference: string; rotation: number } & Rect)[];
  elements: ({ type: (typeof TYPES_ELEMENT_PLAN)[number]; libelle?: string } & Rect)[];
  cotes: { de: { x: number; y: number }; a: { x: number; y: number }; libelle: string }[];
};

function depuisMarche(m: MarcheDetail): Brouillon {
  const p = m.plan as Partial<PlanMarche>;
  if (typeof p.largeur === 'number' && typeof p.hauteur === 'number') {
    return { largeur: p.largeur, hauteur: p.hauteur, zones: p.zones ?? [], emplacements: p.emplacements ?? [], elements: p.elements ?? [], cotes: p.cotes ?? [] };
  }
  // Plan de départ : une bande par zone, les emplacements en rangées de huit (4 m par case).
  const parRangee = 8;
  const largeur = parRangee * 4 + 2;
  let y = 0;
  const zones: Brouillon['zones'] = [];
  const emplacements: Brouillon['emplacements'] = [];
  for (const z of m.zones) {
    const siens = m.emplacements.filter((e) => e.zone.id === z.id);
    const hauteur = Math.max(1, Math.ceil(siens.length / parRangee)) * 4 + 2;
    zones.push({ code: z.code, x: 0, y, largeur, hauteur });
    siens.forEach((e, i) => emplacements.push({ reference: e.reference, x: 1 + (i % parRangee) * 4, y: y + 1 + Math.floor(i / parRangee) * 4, largeur: 3, hauteur: 3, rotation: 0 }));
    y += hauteur + 2;
  }
  return { largeur, hauteur: Math.max(y, 4), zones, emplacements, elements: [], cotes: [] };
}

const nombre = (v: string) => (v.trim() === '' ? 0 : Number(v.replace(',', '.')));

function ChampNombre({ libelle, valeur, onChange }: { libelle: string; valeur: number; onChange: (n: number) => void }) {
  return <input type="number" step="0.5" min="0" aria-label={libelle} value={Number.isFinite(valeur) ? valeur : ''} onChange={(e) => onChange(nombre(e.target.value))} />;
}

function LigneRect<T extends Rect>({ libelle, r, onChange, onRetirer, supplement }: { libelle: string; r: T; onChange: (r: T) => void; onRetirer?: () => void; supplement?: ReactNode }) {
  return (
    <tr>
      <th scope="row" className="cc-mono">
        {libelle}
      </th>
      <td>
        <ChampNombre libelle={`${libelle} : x`} valeur={r.x} onChange={(x) => onChange({ ...r, x })} />
      </td>
      <td>
        <ChampNombre libelle={`${libelle} : y`} valeur={r.y} onChange={(y) => onChange({ ...r, y })} />
      </td>
      <td>
        <ChampNombre libelle={`${libelle} : largeur`} valeur={r.largeur} onChange={(largeur) => onChange({ ...r, largeur })} />
      </td>
      <td>
        <ChampNombre libelle={`${libelle} : hauteur`} valeur={r.hauteur} onChange={(hauteur) => onChange({ ...r, hauteur })} />
      </td>
      <td>{supplement}</td>
      <td>
        {onRetirer ? (
          <Bouton petit variante="discret" onClick={onRetirer} aria-label={`Retirer ${libelle} du plan`}>
            Retirer
          </Bouton>
        ) : null}
      </td>
    </tr>
  );
}

export function EditeurPlan({ marche: m, onFermer }: { marche: MarcheDetail; onFermer: () => void }) {
  const [b, setB] = useState<Brouillon>(() => depuisMarche(m));
  const [problemes, setProblemes] = useState<string[]>([]);
  const [aPlacer, setAPlacer] = useState('');
  const nonPlaces = m.emplacements.filter((e) => !b.emplacements.some((p) => p.reference === e.reference));
  const action = useAction({
    executer: () => {
      const v = schemaPlanMarche.safeParse({ unite: 'm', ...b });
      if (!v.success) {
        setProblemes(v.error.issues.map((i) => `${i.path.join('.')} : ${i.message}`));
        return Promise.reject(new Error('Plan incomplet'));
      }
      setProblemes([]);
      return modifierPlan(m.id, v.data, m.plan_version);
    },
    succes: 'Plan enregistré.',
    invalider: INVALIDER_MARCHES,
    apres: onFermer,
  });
  const erreur = action.erreur instanceof ErreurApi ? action.erreur : null;
  const majZone = (i: number, z: Brouillon['zones'][number]) => setB((s) => ({ ...s, zones: s.zones.map((x, j) => (j === i ? z : x)) }));
  const majEmplacement = (i: number, e: Brouillon['emplacements'][number]) => setB((s) => ({ ...s, emplacements: s.emplacements.map((x, j) => (j === i ? e : x)) }));
  const majElement = (i: number, e: Brouillon['elements'][number]) => setB((s) => ({ ...s, elements: s.elements.map((x, j) => (j === i ? e : x)) }));

  return (
    <Panneau
      titre="Éditeur du plan"
      niveau={2}
      actions={
        <>
          <Bouton variante="secondaire" onClick={onFermer}>
            Annuler
          </Bouton>
          <Bouton variante="principal" chargement={action.enCours} onClick={() => action.lancer(undefined)}>
            Enregistrer le plan
          </Bouton>
        </>
      }
    >
      <p className={styles.aide}>Positions et tailles en mètres depuis le coin supérieur gauche du plan ; version enregistrée {m.plan_version}. Les zones et les emplacements doivent exister sur le marché.</p>
      <div className={styles.barreFiltres}>
        <Champ libelle="Largeur du plan (m)" type="number" min={1} step={0.5} value={b.largeur} onChange={(e) => setB((s) => ({ ...s, largeur: nombre(e.target.value) }))} />
        <Champ libelle="Hauteur du plan (m)" type="number" min={1} step={0.5} value={b.hauteur} onChange={(e) => setB((s) => ({ ...s, hauteur: nombre(e.target.value) }))} />
      </div>
      <table className={styles.tableauPlan}>
        <caption className="cc-etiquette">Zones et emplacements</caption>
        <thead>
          <tr>
            <th scope="col">Élément</th>
            <th scope="col">x</th>
            <th scope="col">y</th>
            <th scope="col">Largeur</th>
            <th scope="col">Hauteur</th>
            <th scope="col">Détail</th>
            <th scope="col">Action</th>
          </tr>
        </thead>
        <tbody>
          {b.zones.map((z, i) => (
            <LigneRect key={`z-${z.code}`} libelle={`Zone ${z.code}`} r={z} onChange={(r) => majZone(i, r)} onRetirer={() => setB((s) => ({ ...s, zones: s.zones.filter((_, j) => j !== i) }))} />
          ))}
          {b.emplacements.map((e, i) => (
            <LigneRect
              key={`e-${e.reference}`}
              libelle={e.reference}
              r={e}
              onChange={(r) => majEmplacement(i, r)}
              onRetirer={() => setB((s) => ({ ...s, emplacements: s.emplacements.filter((_, j) => j !== i) }))}
              supplement={<input type="number" step="15" min="-180" max="180" aria-label={`${e.reference} : rotation en degrés`} value={e.rotation} onChange={(ev) => majEmplacement(i, { ...e, rotation: nombre(ev.target.value) })} />}
            />
          ))}
          {b.elements.map((el, i) => (
            <LigneRect
              key={`el-${i}`}
              libelle={el.libelle || el.type}
              r={el}
              onChange={(r) => majElement(i, r)}
              onRetirer={() => setB((s) => ({ ...s, elements: s.elements.filter((_, j) => j !== i) }))}
              supplement={<input type="text" aria-label={`Libellé de l'élément ${i + 1}`} value={el.libelle ?? ''} onChange={(ev) => majElement(i, { ...el, libelle: ev.target.value || undefined })} />}
            />
          ))}
        </tbody>
      </table>
      <div className={styles.barreFiltres}>
        {m.zones.filter((z) => !b.zones.some((p) => p.code === z.code)).length > 0 ? (
          <Bouton petit variante="secondaire" onClick={() => setB((s) => ({ ...s, zones: [...s.zones, ...m.zones.filter((z) => !s.zones.some((p) => p.code === z.code)).map((z) => ({ code: z.code, x: 0, y: 0, largeur: 10, hauteur: 6 }))] }))}>
            Placer les zones manquantes
          </Bouton>
        ) : null}
        {nonPlaces.length > 0 ? (
          <>
            <Selection libelle="Emplacement à placer" optionVide="Choisir" options={nonPlaces.map((e) => ({ valeur: e.reference, libelle: `${e.reference} (zone ${e.zone.code ?? '?'})` }))} value={aPlacer} onChange={(e) => setAPlacer(e.target.value)} />
            <Bouton petit variante="secondaire" disabled={!aPlacer} onClick={() => aPlacer && setB((s) => ({ ...s, emplacements: [...s.emplacements, { reference: aPlacer, x: 1, y: 1, largeur: 3, hauteur: 3, rotation: 0 }] }))}>
              Placer
            </Bouton>
          </>
        ) : null}
        <Selection
          libelle="Ajouter un élément de repère"
          optionVide="Choisir"
          options={TYPES_ELEMENT_PLAN.map((t) => ({ valeur: t, libelle: t }))}
          value=""
          onChange={(e) => e.target.value && setB((s) => ({ ...s, elements: [...s.elements, { type: e.target.value as Brouillon['elements'][number]['type'], x: 0, y: 0, largeur: 4, hauteur: 2 }] }))}
        />
      </div>
      {problemes.length > 0 ? (
        <Bandeau type="erreur">
          Plan incomplet : {problemes.join(' ; ')}
        </Bandeau>
      ) : null}
      {erreur?.code === 'plan_modifie' ? <Bandeau type="erreur">Le plan a été modifié par quelqu&apos;un d&apos;autre depuis l&apos;ouverture de l&apos;éditeur : rechargez la fiche avant de reprendre vos modifications.</Bandeau> : null}
      {erreur && erreur.code !== 'plan_modifie' ? (
        <Bandeau type="erreur">
          {erreur.message}
          {erreur.champs.length > 0 ? ` : ${erreur.champs.map((c) => `${c.champ} (${c.message})`).join(', ')}` : ''}
        </Bandeau>
      ) : null}
    </Panneau>
  );
}
